import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { getSampleStatus, clearSamplePortfolio, importRealData, addOneAccountFromSample, SAMPLE_PHASE_KEY } from "@/lib/sampleData";
import { Sparkle, UploadSimple, X, PlusCircle } from "@phosphor-icons/react";

// Moment 4 — persistent invitational dock (replaces the old defensive amber ribbon).
// Sits at the bottom on every page while a sample portfolio is loaded, always pointing
// the user toward importing their own data. Hidden during the loader/reveal/tour.
export function SampleInviteBanner() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(false); // an experience overlay is running

  const refresh = () => getSampleStatus().then(setStatus).catch(() => setStatus(null));

  useEffect(() => {
    refresh();
    const onPhase = () => setActive(!!sessionStorage.getItem(SAMPLE_PHASE_KEY));
    onPhase();
    window.addEventListener("recoveros:sample-phase", onPhase);
    window.addEventListener("recoveros:sample-done", refresh);
    return () => {
      window.removeEventListener("recoveros:sample-phase", onPhase);
      window.removeEventListener("recoveros:sample-done", refresh);
    };
  }, []);

  if (!status || !status.loaded || active) return null;

  const clear = async () => {
    setBusy(true);
    const t = toast.loading("Clearing sample data…");
    try {
      await clearSamplePortfolio();
      toast.success("Sample data cleared", { id: t });
      setTimeout(() => window.location.reload(), 400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not clear sample data", { id: t });
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        data-testid="sample-invite-banner"
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 30 }}
        transition={{ type: "spring", stiffness: 260, damping: 26 }}
        className="fixed bottom-5 left-1/2 -translate-x-1/2 z-40 w-[92%] max-w-3xl bg-white border border-[#0B3D91] rounded-sm flex flex-wrap items-center gap-x-5 gap-y-3 px-5 py-3.5"
        style={{ boxShadow: "0 14px 44px rgba(11,61,145,0.22)" }}
      >
        <div className="flex items-center gap-2.5 flex-1 min-w-[220px]">
          <div className="h-9 w-9 rounded-sm bg-[#16A34A]/10 flex items-center justify-center shrink-0">
            <Sparkle size={18} weight="fill" className="text-[#16A34A]" />
          </div>
          <p className="text-sm text-gray-800 font-medium leading-snug">
            You're exploring a <strong>sample portfolio</strong>. Ready to see <em>your</em> stuck money?
          </p>
        </div>
        <div className="flex items-center gap-3 ml-auto flex-wrap justify-end">
          <button onClick={clear} disabled={busy} data-testid="sample-clear-btn"
            className="inline-flex items-center gap-1 text-xs font-semibold text-gray-400 hover:text-gray-700 transition-colors disabled:opacity-60">
            <X size={13} weight="bold" /> {busy ? "Clearing…" : "Clear demo"}
          </button>
          <button onClick={() => addOneAccountFromSample("invite_banner")}
            data-testid="sample-add-one-cta"
            className="inline-flex items-center gap-1.5 border-2 border-[#0B3D91] text-[#0B3D91] rounded-sm px-3.5 py-2 text-sm font-semibold hover:bg-[#0B3D91]/[0.06] transition-colors shrink-0">
            <PlusCircle size={14} weight="bold" /> Add one account
          </button>
          <button onClick={() => importRealData(navigate, "invite_banner")}
            data-testid="import-real-data-cta"
            className="inline-flex items-center gap-1.5 bg-[#16A34A] text-white rounded-sm px-5 py-2.5 text-sm font-bold hover:bg-[#138a3e] hover:-translate-y-0.5 transition-[transform,background-color] duration-200 shrink-0">
            <UploadSimple size={15} weight="bold" /> Import my real data
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
